import React, { useState } from 'react'
import { Text, Heading, Button, LoadingSpinner, Input } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase, database } from '../lib/supabase'
import DocumentConverter from '../utils/documentConverter'
import {
  CloudArrowUpIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

const CCTImportar = () => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [uploadMessage, setUploadMessage] = useState('')

  // Estados do formulário
  const [cnpjPatronal, setCnpjPatronal] = useState('')
  const [cnpjLaboral, setCnpjLaboral] = useState('')
  const [formErrors, setFormErrors] = useState<{cnpjPatronal?: string, cnpjLaboral?: string}>({})
  
  // Estados para conversão de documento
  const [documentContent, setDocumentContent] = useState<{
    html: string
    text: string
    wordCount: number
  } | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      const file = files[0]
      if (isValidFileType(file)) {
        setSelectedFile(file)
        setUploadStatus('idle')
        setUploadMessage('')
      } else {
        setUploadStatus('error')
        setUploadMessage('Tipo de arquivo não suportado. Use PDF, DOC ou DOCX.')
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (isValidFileType(file)) {
        setSelectedFile(file)
        setUploadStatus('idle')
        setUploadMessage('')
        setDocumentContent(null)
        setShowPreview(false)
        // Converter documento automaticamente
        convertDocument(file)
      } else {
        setUploadStatus('error')
        setUploadMessage('Tipo de arquivo não suportado. Use PDF, DOC ou DOCX.')
      }
    }
  }

  const convertDocument = async (file: File) => {
    setIsConverting(true)
    setUploadMessage('Convertendo documento...')
    
    try {
      const result = await DocumentConverter.convertToHtml(file)
      
      if (result.success) {
        const wordCount = result.text.split(/\s+/).length
        
        setDocumentContent({
          html: result.html,
          text: result.text,
          wordCount
        })
        
        setUploadMessage(`Documento convertido com sucesso! ${wordCount} palavras encontradas.`)
      } else {
        setUploadStatus('error')
        setUploadMessage(`Erro na conversão: ${result.error}`)
      }
    } catch (error: any) {
      setUploadStatus('error')
      setUploadMessage(`Erro na conversão: ${error.message}`)
    } finally {
      setIsConverting(false)
    }
  }

  const isValidFileType = (file: File) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    return validTypes.includes(file.type)
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    if (extension === 'pdf') {
      return <DocumentArrowUpIcon className="h-8 w-8 text-red-500" />
    } else if (extension === 'doc' || extension === 'docx') {
      return <DocumentArrowUpIcon className="h-8 w-8 text-blue-500" />
    }
    return <DocumentArrowUpIcon className="h-8 w-8 text-gray-500" />
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    if (!validateForm()) {
      setUploadStatus('error')
      setUploadMessage('Por favor, corrija os erros no formulário antes de continuar.')
      return
    }

    setIsUploading(true)
    setUploadStatus('idle')

    try {
      // Gerar nome único para o arquivo
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const fileExtension = selectedFile.name.split('.').pop()
      const fileName = `cct_${timestamp}.${fileExtension}`
      
      // Caminho no bucket: ccts/arquivo.ext
      const filePath = fileName
      
      // Verificar se é PDF - se for, fazer upload direto sem conversão
      if (selectedFile.type === 'application/pdf') {
        // Upload do arquivo PDF original
        const { data, error } = await supabase.storage
          .from('documents_temp')
          .upload(`ccts/${fileName}`, selectedFile)
          
        if (error) {
          throw new Error(`Erro no upload: ${error.message}`)
        }

        // Salvar metadados no banco de dados
        const metadata = {
          cnpj_entidade_patronal: cnpjPatronal,
          cnpj_entidade_laboral: cnpjLaboral,
          file_name: fileName,
          file_path: `ccts/${fileName}`,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
          status: 'Importado'
        }

        const { error: dbError } = await database.insert('ccts_importadas', metadata)

        if (dbError) {
          // Não falhar o upload por causa dos metadados
        }

        setUploadStatus('success')
        setUploadMessage(`Arquivo PDF importado com sucesso! Arquivo salvo em: ccts/${fileName}`)
        setSelectedFile(null)
        setCnpjPatronal('')
        setCnpjLaboral('')
        setFormErrors({})
        setDocumentContent(null)
        setShowPreview(false)
        return
      }

      // Para outros tipos de arquivo (DOC, DOCX), fazer conversão
      let htmlContent = ''
      if (!documentContent) {
        const conversionResult = await DocumentConverter.convertToHtml(selectedFile)
        if (conversionResult.success) {
          htmlContent = conversionResult.html
        } else {
          throw new Error(`Erro na conversão: ${conversionResult.error}`)
        }
      } else {
        htmlContent = documentContent.html
      }

      // Criar arquivo TXT
      const txtBlob = new Blob([htmlContent], { type: 'text/plain' })
      const txtFileName = fileName.replace(/\.[^/.]+$/, '.txt')

      // Upload do arquivo TXT para bucket documents_temp na pasta ccts
      const { data, error } = await supabase.storage
        .from('documents_temp')
        .upload(`ccts/${txtFileName}`, txtBlob)
      
      if (error) {
        throw new Error(`Erro no upload: ${error.message}`)
      }
      
      // Salvar metadados no banco de dados
      const metadata = {
        cnpj_entidade_patronal: cnpjPatronal,
        cnpj_entidade_laboral: cnpjLaboral,
        file_name: txtFileName, // Nome do arquivo TXT
        file_path: `ccts/${txtFileName}`, // Caminho do arquivo TXT
        file_size: txtBlob.size, // Tamanho do arquivo TXT
        file_type: 'text/plain', // Tipo do arquivo TXT
        status: 'Importado'
      }
      
      const { error: dbError } = await database.insert('ccts_importadas', metadata)

      if (dbError) {
        // Não falhar o upload por causa dos metadados
      }
      
      setUploadStatus('success')
      setUploadMessage(`Documento convertido e importado com sucesso! Arquivo TXT salvo em: ccts/${txtFileName}`)
      setSelectedFile(null)
      setCnpjPatronal('')
      setCnpjLaboral('')
      setFormErrors({})
      // Limpar preview do documento
      setDocumentContent(null)
      setShowPreview(false)
    } catch (error: any) {
      setUploadStatus('error')
      setUploadMessage(`Erro ao importar arquivo: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadStatus('idle')
    setUploadMessage('')
  }

  // Função para formatar CNPJ
  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    // Limitar a 14 dígitos
    const limitedNumbers = numbers.slice(0, 14)
    return limitedNumbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  // Função para validar CNPJ
  const validateCNPJ = (cnpj: string) => {
    const numbers = cnpj.replace(/\D/g, '')
    if (numbers.length === 0) return 'CNPJ é obrigatório'
    if (numbers.length < 14) return 'CNPJ deve ter 14 dígitos'
    if (numbers.length > 14) return 'CNPJ deve ter exatamente 14 dígitos'
    
    // Validação básica de CNPJ (não todos iguais)
    if (/^(\d)\1{13}$/.test(numbers)) {
      return 'CNPJ inválido (não pode ter todos os dígitos iguais)'
    }
    
    return null
  }

  // Função para validar formulário
  const validateForm = () => {
    const errors: {cnpjPatronal?: string, cnpjLaboral?: string} = {}
    
    if (!cnpjPatronal.trim()) {
      errors.cnpjPatronal = 'CNPJ Patronal é obrigatório'
    } else {
      const patronalError = validateCNPJ(cnpjPatronal)
      if (patronalError) errors.cnpjPatronal = patronalError
    }
    
    if (!cnpjLaboral.trim()) {
      errors.cnpjLaboral = 'CNPJ Laboral é obrigatório'
    } else {
      const laboralError = validateCNPJ(cnpjLaboral)
      if (laboralError) errors.cnpjLaboral = laboralError
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      default:
        return null
    }
  }

  const getStatusColor = () => {
    switch (uploadStatus) {
      case 'success':
        return 'text-green-600 dark:text-green-400'
      case 'error':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-gray-600 dark:text-gray-400'
    }
  }

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <PermissionGuard
          moduleName="ccts_importar"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Importar CCTs
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Faça upload de documentos PDF ou DOC com Convenções Coletivas
              </Text>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                {/* Formulário de Dados */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Dados da Convenção Coletiva
                  </Heading>
                  <Text size="sm" color="muted" className="mb-6">
                    Preencha os dados obrigatórios antes de fazer o upload do documento
                  </Text>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* CNPJ Patronal */}
                    <div>
                      <label htmlFor="cnpjPatronal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        CNPJ Patronal *
                      </label>
                      <Input
                        id="cnpjPatronal"
                        type="text"
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        value={cnpjPatronal}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const formatted = formatCNPJ(e.target.value)
                          setCnpjPatronal(formatted)
                          // Limpar erro quando usuário começar a digitar
                          if (formErrors.cnpjPatronal) {
                            setFormErrors(prev => ({ ...prev, cnpjPatronal: undefined }))
                          }
                        }}
                        className={`w-full ${formErrors.cnpjPatronal ? 'border-red-500 focus:border-red-500' : ''}`}
                      />
                      {formErrors.cnpjPatronal && (
                        <Text size="sm" className="text-red-600 dark:text-red-400 mt-1">
                          {formErrors.cnpjPatronal}
                        </Text>
                      )}
                    </div>

                    {/* CNPJ Laboral */}
                    <div>
                      <label htmlFor="cnpjLaboral" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        CNPJ Laboral *
                      </label>
                      <Input
                        id="cnpjLaboral"
                        type="text"
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        value={cnpjLaboral}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const formatted = formatCNPJ(e.target.value)
                          setCnpjLaboral(formatted)
                          // Limpar erro quando usuário começar a digitar
                          if (formErrors.cnpjLaboral) {
                            setFormErrors(prev => ({ ...prev, cnpjLaboral: undefined }))
                          }
                        }}
                        className={`w-full ${formErrors.cnpjLaboral ? 'border-red-500 focus:border-red-500' : ''}`}
                      />
                      {formErrors.cnpjLaboral && (
                        <Text size="sm" className="text-red-600 dark:text-red-400 mt-1">
                          {formErrors.cnpjLaboral}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Area */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Upload do Documento
                  </Heading>
                  <Text size="sm" color="muted" className="mb-6">
                    Selecione ou arraste o arquivo PDF ou DOC para fazer o upload
                  </Text>

                  {/* Drag and Drop Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragOver
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center">
                          {getFileIcon(selectedFile.name)}
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                            {selectedFile.name}
                          </Text>
                          <Text size="sm" color="muted">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </div>
                        <Button
                          onClick={handleRemoveFile}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          Remover Arquivo
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto" />
                        <div>
                          <Text size="lg" className="text-gray-900 dark:text-white font-medium">
                            Arraste o arquivo aqui ou clique para selecionar
                          </Text>
                          <Text size="sm" color="muted" className="mt-2">
                            Suporta PDF, DOC, DOCX (máximo 50MB)
                          </Text>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                        >
                          Selecionar Arquivo
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  {selectedFile && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        onClick={handleUpload}
                        disabled={isUploading}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
                      >
                        {isUploading ? (
                          <>
                            <LoadingSpinner size="sm" className="mr-2" />
                            Importando...
                          </>
                        ) : (
                          <>
                            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                            Importar Arquivo
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Status Message */}
                  {uploadMessage && (
                    <div className="mt-6 p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon()}
                        <Text size="sm" className={getStatusColor()}>
                          {uploadMessage}
                        </Text>
                      </div>
                    </div>
                  )}

                  {/* Document Preview */}
                  {documentContent && (
                    <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                            Preview do Documento
                          </Heading>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Text size="sm" color="muted">
                            {documentContent.wordCount} palavras
                          </Text>
                          <Button
                            onClick={() => setShowPreview(!showPreview)}
                            variant="outline"
                            size="sm"
                          >
                            {showPreview ? 'Ocultar' : 'Mostrar'} Preview
                          </Button>
                        </div>
                      </div>
                      
                      {showPreview && (
                        <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                          <div 
                            className="prose prose-sm max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: documentContent.html }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                  <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <Heading as="h3" size="2xl" className="text-blue-900 dark:text-blue-100 mb-2">
                        Instruções para Importação
                      </Heading>
                      <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                        <p>• Preencha os CNPJs da entidade patronal e laboral antes do upload</p>
                        <p>• O arquivo deve estar no formato PDF, DOC ou DOCX</p>
                        <p>• <strong>Recomendado:</strong> Use DOCX ou PDF para melhor compatibilidade</p>
                        <p>• Arquivos DOC antigos podem ter limitações na conversão</p>
                        <p>• O documento deve conter a Convenção Coletiva completa</p>
                        <p>• O arquivo não deve exceder 50MB</p>
                        <p>• Certifique-se de que o documento está legível e bem formatado</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CCTImportar